import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTeamLogo, normalizeTeamName } from '../utils/teamUtils';
import Select from 'react-select';

const TeamDashboard = ({ players, isDarkMode }) => {
  const { teamName } = useParams();
  const navigate = useNavigate();
  const [selectedYears, setSelectedYears] = useState(['2025', '2024']);
  const [selectedTeam, setSelectedTeam] = useState(teamName || 'Broncos');
  const [selectedVenue, setSelectedVenue] = useState('All');
  const [selectedOpponent, setSelectedOpponent] = useState('All');
  const [selectedWithout, setSelectedWithout] = useState('All');
  const [selectedGameType, setSelectedGameType] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [playerStats, setPlayerStats] = useState([]);
  const [originalPlayerStats, setOriginalPlayerStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: 'playerName',
    direction: 'asc'
  });
  
  // Available years for selection
  const years = ['2025', '2024', '2023', '2022', '2021', '2020'];
  
  // All NRL teams
  const teams = [
    'Broncos', 'Bulldogs', 'Cowboys', 'Dolphins', 'Dragons', 'Eels',
    'Knights', 'Panthers', 'Raiders', 'Rabbitohs', 'Roosters', 'Sea Eagles',
    'Sharks', 'Storm', 'Tigers', 'Warriors'
  ];
  
  // Stat columns to display based on NRL statistics
  const statColumns = [
    { id: 'all_run_metres', label: 'RUN METRES' },
    { id: 'tackles_made', label: 'TACKLES' },
    { id: 'tries', label: 'TRIES' },
    { id: 'line_breaks', label: 'LINE BREAKS' },
    { id: 'tackle_breaks', label: 'TACKLE BREAKS' }, 
    { id: 'kick_metres', label: 'KICK METRES' },
    { id: 'offloads', label: 'OFFLOADS' },
    { id: 'try_assists', label: 'TRY ASSISTS' },
    { id: 'points', label: 'POINTS' },
    { id: 'dummy_passes', label: 'DUMMY PASSES' },
    { id: 'errors', label: 'ERRORS' },
    { id: 'penalties', label: 'PENALTIES' },
    { id: 'performance_points', label: 'PP' }
  ];
  
  // Update URL when team changes
  useEffect(() => {
    if (teamName !== selectedTeam && selectedTeam) {
      navigate(`/team-dashboard/${selectedTeam}`);
    }
  }, [selectedTeam, teamName, navigate]);
  
  // Helper function to calculate player stats
  const calculatePlayerStats = (filteredPlayers, uniquePlayerNames) => {
    return uniquePlayerNames.map(name => {
      const playerGames = filteredPlayers.filter(p => p.player_name === name);
      if (playerGames.length === 0) return null;
      
      const position = playerGames[0].position || '';
      const stats = {};
      
      statColumns.forEach(({ id }) => {
        if (!id) return;
        
        if (id === 'performance_points') {
          const totalPP = playerGames.reduce((sum, game) => {
            let pp = 0;
            pp += (parseFloat(game['points']) || 0) * 4;
            pp += (parseFloat(game['try_assists']) || 0) * 10;
            const runMeters = parseFloat(game['all_run_metres']) || 0;
            pp += Math.floor(runMeters / 10);
            pp += (parseFloat(game['line_breaks']) || 0) * 5;
            pp += (parseFloat(game['tackles_made']) || 0);
            return sum + pp;
          }, 0);
          
          stats[id] = parseFloat((totalPP / playerGames.length).toFixed(1));
        } else {
          const total = playerGames.reduce((sum, game) => {
            const value = game[id] === null || game[id] === undefined || game[id] === '-' 
              ? 0 
              : parseFloat(game[id]) || 0;
            return sum + value;
          }, 0);
          
          stats[id] = parseFloat((total / playerGames.length).toFixed(1));
        }
      });
      
      return {
        id: name,
        playerName: name,
        position,
        gamesPlayed: playerGames.length,
        ...stats
      };
    }).filter(Boolean);
  };

  // Calculate original stats when team/year changes
  useEffect(() => {
    if (!players || players.length === 0) return;
    
    // Filter by selected years and team for original stats
    let baseFilteredPlayers = players.filter(p => 
      selectedYears.includes(String(p.year)) && 
      p.player_team === selectedTeam
    );
    
    const uniquePlayerNames = [...new Set(baseFilteredPlayers.map(p => p.player_name))];
    const originalStats = calculatePlayerStats(baseFilteredPlayers, uniquePlayerNames);
    setOriginalPlayerStats(originalStats);
  }, [players, selectedYears, selectedTeam]);

  // Process data when filters change
  useEffect(() => {
    if (!players || players.length === 0) return;
    
    setLoading(true);
    
    try {
      // Filter by selected years
      let filteredPlayers = players.filter(p => selectedYears.includes(String(p.year)));
      
      // Filter by selected team
      filteredPlayers = filteredPlayers.filter(p => p.player_team === selectedTeam);
      
      // Filter by venue if selected
      if (selectedVenue !== 'All') {
        filteredPlayers = filteredPlayers.filter(p => p.venue === selectedVenue);
      }
      
      // Filter by opponent if selected
      if (selectedOpponent !== 'All') {
        filteredPlayers = filteredPlayers.filter(p => {
          // Handle is_home_game possibly being a string or boolean
          const isHomeGame = typeof p.is_home_game === 'string' 
            ? p.is_home_game === 'true' 
            : !!p.is_home_game;
          
          return isHomeGame 
            ? p.away_team === selectedOpponent 
            : p.home_team === selectedOpponent;
        });
      }
      
      // Filter by game type (win/loss)
      if (selectedGameType !== 'All') {
        filteredPlayers = filteredPlayers.filter(p => {
          // Handle is_home_game possibly being a string or boolean
          const isHomeGame = typeof p.is_home_game === 'string' 
            ? p.is_home_game === 'true' 
            : !!p.is_home_game;
          
          const homeScore = parseInt(p.home_score || 0);
          const awayScore = parseInt(p.away_score || 0);
          
          const isWin = isHomeGame 
            ? homeScore > awayScore 
            : awayScore > homeScore;
            
          return selectedGameType === 'Win' ? isWin : !isWin;
        });
      }
      
      // Filter by location (home/away)
      if (selectedLocation !== 'All') {
        // Convert string 'Home'/'Away' to boolean and handle is_home_game being a string
        const isHome = selectedLocation === 'Home';
        filteredPlayers = filteredPlayers.filter(p => {
          const playerIsHome = typeof p.is_home_game === 'string' 
            ? p.is_home_game === 'true' 
            : !!p.is_home_game;
          return playerIsHome === isHome;
        });
      }
      
      // Filter without player if selected
      if (selectedWithout !== 'All') {
        // Get all games that include the selected player
        const gamesWithPlayer = new Set(
          players
            .filter(p => p.player_name === selectedWithout && p.player_team === selectedTeam)
            .map(p => `${p.round}_${p.player_team}_${p.home_team}_${p.away_team}`)
        );
        
        // Only include games where the selected player didn't play
        filteredPlayers = filteredPlayers.filter(p => 
          !gamesWithPlayer.has(`${p.round}_${p.player_team}_${p.home_team}_${p.away_team}`)
        );
      }
      
      // Get unique player names
      const uniquePlayerNames = [...new Set(filteredPlayers.map(p => p.player_name))];
      
      const playerStatsData = calculatePlayerStats(filteredPlayers, uniquePlayerNames);
      
      // Sort by run metres descending
      playerStatsData.sort((a, b) => b.all_run_metres - a.all_run_metres);
      
      setPlayerStats(playerStatsData);
    } catch (error) {
      console.error('Error processing team dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [players, selectedYears, selectedTeam, selectedVenue, selectedOpponent, selectedGameType, selectedLocation, selectedWithout]);
  
  // Get list of venues for the selected team
  const venues = useMemo(() => {
    if (!players || players.length === 0) return ['All'];
    
    const teamVenues = players
      .filter(p => p.player_team === selectedTeam)
      .map(p => p.venue)
      .filter(Boolean);
    
    return ['All', ...new Set(teamVenues)].sort();
  }, [players, selectedTeam]);
  
  // Get list of players from the selected team (for the "Without" filter)
  const teamPlayers = useMemo(() => {
    if (!players || players.length === 0) return [];
    
    const uniquePlayers = [...new Set(
      players
        .filter(p => 
          p.player_team === selectedTeam && 
          selectedYears.includes(String(p.year))
        )
        .map(p => p.player_name)
    )].filter(Boolean).sort();

    return uniquePlayers;
  }, [players, selectedTeam, selectedYears]);
  
  // Add sort handler function
  const handleSort = (columnId) => {
    setSortConfig(prevConfig => ({
      key: columnId,
      direction: prevConfig.key === columnId && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Add sorting logic to the data
  const sortedPlayerStats = useMemo(() => {
    const sortedData = [...playerStats];
    sortedData.sort((a, b) => {
      const aValue = a[sortConfig.key] || 0;
      const bValue = b[sortConfig.key] || 0;
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      }
      return bValue - aValue;
    });
    return sortedData;
  }, [playerStats, sortConfig]);
  
  // Replace the getColorClass function with this new one
  const getComparisonColor = (currentValue, originalValue, isNegativeGood = false) => {
    if (!originalValue || currentValue === originalValue) {
      return 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100';
    }
    
    const percentDiff = ((currentValue - originalValue) / originalValue) * 100;
    
    // For negative stats (errors, penalties), invert the color logic
    if (isNegativeGood) {
      if (percentDiff < -10) {
        return 'bg-green-100 dark:bg-green-800 text-green-900 dark:text-green-100';
      } else if (percentDiff > 10) {
        return 'bg-red-100 dark:bg-red-800 text-red-900 dark:text-red-100';
      }
    } else {
      if (percentDiff > 10) {
        return 'bg-green-100 dark:bg-green-800 text-green-900 dark:text-green-100';
      } else if (percentDiff < -10) {
        return 'bg-red-100 dark:bg-red-800 text-red-900 dark:text-red-100';
      }
    }
    
    return 'bg-yellow-100 dark:bg-yellow-800 text-yellow-900 dark:text-yellow-100';
  };
  
  return (
    <div className="h-full w-full">
      <div className="mb-3">
        <div className="flex flex-wrap md:flex-nowrap items-center gap-2 mb-2">
          <div className="flex items-center">
            <img 
              src={getTeamLogo(selectedTeam)} 
              alt={selectedTeam} 
              className="w-6 h-6 md:w-8 md:h-8 mr-1.5 object-contain"
              onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/24?text=NRL'; }}
            />
            <h2 className="text-lg md:text-xl font-bold dark:text-white">{normalizeTeamName(selectedTeam)} Dashboard</h2>
          </div>
          
          {/* Team selector - visible on all screen sizes */}
          <select
            value={selectedTeam}
            onChange={e => setSelectedTeam(e.target.value)}
            className="ml-auto px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 flex-shrink-0"
          >
            {teams.map(team => (
              <option key={team} value={team}>{normalizeTeamName(team)}</option>
            ))}
          </select>
        </div>
        
        {/* Main filter row - much more compact on desktop */}
        <div className="flex flex-col md:flex-row md:items-center md:space-x-3 space-y-1.5 md:space-y-0">
          {/* Year Row - now inline on desktop */}
          <div className="flex items-center flex-nowrap overflow-x-auto pb-1 md:pb-0 md:flex-shrink-0">
            {years.map(year => (
              <button
                key={year}
                onClick={() => {
                  setSelectedYears(prev => {
                    if (prev.includes(year)) {
                      if (prev.length === 1) return prev;
                      return prev.filter(y => y !== year);
                    }
                    return [...prev, year];
                  });
                }}
                className={`flex-shrink-0 px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs rounded-md mr-1 ${
                  selectedYears.includes(year)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Win/Loss & Home/Away Filters inline on desktop */}
          <div className="flex items-center space-x-2 md:space-x-3">
            {/* Win/Loss filter */}
            <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                className={`px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs ${
                  selectedGameType === 'All'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setSelectedGameType('All')}
              >
                All
              </button>
              <button
                className={`px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs ${
                  selectedGameType === 'Win'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setSelectedGameType('Win')}
              >
                Win
              </button>
              <button
                className={`px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs ${
                  selectedGameType === 'Loss'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setSelectedGameType('Loss')}
              >
                Loss
              </button>
            </div>

            {/* Home/Away filter */}
            <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                className={`px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs ${
                  selectedLocation === 'All'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setSelectedLocation('All')}
              >
                All
              </button>
              <button
                className={`px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs ${
                  selectedLocation === 'Home'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setSelectedLocation('Home')}
              >
                Home
              </button>
              <button
                className={`px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs ${
                  selectedLocation === 'Away'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => setSelectedLocation('Away')}
              >
                Away
              </button>
            </div>
          </div>

          {/* Venue/Opponent/Without Selects */}
          <div className="flex items-center gap-1.5 md:gap-2 flex-grow">
            <select
              value={selectedVenue}
              onChange={e => setSelectedVenue(e.target.value)}
              className="px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 flex-1 min-w-0"
            >
              {venues.map(venue => (
                <option key={venue} value={venue}>{venue || 'All Venues'}</option>
              ))}
            </select>
            
            <select
              value={selectedOpponent}
              onChange={e => setSelectedOpponent(e.target.value)}
              className="px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 flex-1 min-w-0"
            >
              <option value="All">All Opponents</option>
              {teams.filter(team => team !== selectedTeam).map(team => (
                <option key={team} value={team}>{normalizeTeamName(team)}</option>
              ))}
            </select>
            
            <select
              value={selectedWithout}
              onChange={e => setSelectedWithout(e.target.value)}
              className="px-1.5 py-0.5 md:px-2 md:py-1 text-[10px] md:text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 flex-1 min-w-0"
            >
              <option value="All">Without</option>
              {teamPlayers.map(player => (
                <option key={player} value={player}>w/o {player}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[10px] md:text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-1.5 py-1.5 md:px-3 md:py-2 text-left text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 w-[80px] md:w-[120px]">
                    Player
                  </th>
                  {/* Only show first 6 stats on mobile, all on desktop */}
                  {statColumns.slice(0, window.innerWidth < 768 ? 6 : undefined).map(column => (
                    <th 
                      key={column.id} 
                      className="px-1 py-1.5 md:px-3 md:py-2 text-center text-[10px] md:text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort(column.id)}
                    >
                      <div className="flex items-center justify-center">
                        {column.label}
                        {sortConfig.key === column.id && (
                          <span className="ml-0.5">
                            {sortConfig.direction === 'desc' ? '↓' : '↑'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedPlayerStats.map(player => (
                  <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-1.5 py-1.5 md:px-3 md:py-2 whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-10 w-[80px] md:w-[120px]">
                      <div className="flex flex-col">
                        <div className="text-[10px] md:text-sm font-medium text-gray-900 dark:text-white">
                          {player.playerName}
                        </div>
                        <div className="text-[8px] md:text-[10px] text-gray-500 dark:text-gray-400">
                          {player.position} • {player.gamesPlayed} games
                        </div>
                      </div>
                    </td>
                    {/* Only show first 6 stats on mobile, all on desktop */}
                    {statColumns.slice(0, window.innerWidth < 768 ? 6 : undefined).map(column => {
                      const originalStat = originalPlayerStats.find(p => p.id === player.id)?.[column.id] || 0;
                      const showOriginal = (
                        selectedVenue !== 'All' || 
                        selectedOpponent !== 'All' || 
                        selectedGameType !== 'All' || 
                        selectedLocation !== 'All' || 
                        selectedWithout !== 'All'
                      );
                      
                      const currentValue = player[column.id] || 0;
                      const isNegativeStat = ['errors', 'penalties'].includes(column.id);
                      const colorClass = showOriginal 
                        ? getComparisonColor(currentValue, originalStat, isNegativeStat)
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100';
                      
                      return (
                        <td key={column.id} className="px-1 py-0.5 md:px-2 md:py-2 whitespace-nowrap text-center">
                          <div className={`rounded-full inline-block py-0.5 px-1.5 md:py-1 md:px-2 min-w-[32px] md:min-w-[40px] text-[10px] md:text-sm font-medium ${colorClass}`}>
                            {currentValue || '0.0'}
                            {showOriginal && originalStat !== currentValue && (
                              <span className="text-[8px] md:text-[10px] ml-0.5 opacity-75">
                                ({originalStat})
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDashboard; 